import { DotLegalButton, DotLegalCheckbox } from "@dotlegal/dotlegal-ui-components";
import DotLegalCollapse from "../../../../common/components/dotLegalCollapse/DotLegalCollapse";
import DotLegalStepHeader from "../../../../common/components/dotLegalStepHeader/DotLegalStepHeader";
import { useTranslation } from "../../../../localization/useTranslation";
import StepSegment from "../../../../processingActivity/stepSegment/StepSegment";
import useSystemRelationStepHooks from "./SystemRelationStep.hooks";
import SystemRelationSelector from "./systemRelationSelector/SystemRelationSelector";
import { useSystemRelationStepStyles } from "./SystemRelationStep.styles";
import { Box } from "@mui/system";
import { Skeleton, Typography } from "@mui/material";
import { useUrlProvider } from "../../../../useUrlProvider";
import { dotlegalLinkIcon } from "../../../../common/icons";

export interface ISystemRelationStep {
    id: string;
    relatedSystems: Array<string>;
    isLoading: boolean;
    disableSystemRelations: boolean;
    onDisableSystemRelationsChange: (checked: boolean) => void;
    readOnly: boolean;
}

function SystemRelationStep(props: ISystemRelationStep) {
    const { translateString } = useTranslation();
    const { getSystemEditUrl } = useUrlProvider();
    const {
        getExpandedId,
        setExpandedId,
        selectableItems,
        isLoading,
        isDetailsLoading,
        handleRelationChange,
        isAddingNewRelation,
        setIsAddingNewRelation,
        onExpandClick,
        handleRemoveSystem,
        getDetailForSystem,
    } = useSystemRelationStepHooks(props);
    const styles = useSystemRelationStepStyles();

    function getSelectableItems(existingSystemId?: string) {
        return selectableItems?.filter((x) => !props.relatedSystems.includes(x.id) || existingSystemId === x.id);
    }

    const getLinkIcon = () => {
        return <Box sx={{ "& svg": { height: 20, width: 20 } }}>{dotlegalLinkIcon}</Box>;
    };

    function getSystemName(systemId: string) {
        return selectableItems?.find((x) => x.id === systemId)?.name ?? "";
    }

    function getSelectedSystems() {
        return props.relatedSystems.map((s) => {
            var name = getSystemName(s);
            return (
                <DotLegalCollapse
                    disabled={props.readOnly}
                    centerContent={<Typography sx={styles.collapseHeaderText}>{name}</Typography>}
                    sx={{ marginBottom: 2 }}
                    key={s}
                    isExpanded={s === getExpandedId()}
                    onExpandClick={() => onExpandClick(s)}
                    onRemoveClick={() => handleRemoveSystem(s)}
                    removeItemText={translateString("removeAsset")}
                    collapseIcon={{
                        icon: getLinkIcon(),
                        iconHoverText: translateString("goToWithName", { name: name }),
                        href: `${getSystemEditUrl(s)}?step=5#${props.id}`,
                    }}
                >
                    <SystemRelationSelector
                        disabled={props.readOnly}
                        isLoading={isLoading}
                        isDetailsLoading={isDetailsLoading}
                        selectedSystemId={s}
                        selectedDetail={getDetailForSystem(s)}
                        selectableItems={getSelectableItems(s)}
                        onAssetChange={(id) => handleRelationChange(id)}
                    />
                </DotLegalCollapse>
            );
        });
    }

    function getLoadingState() {
        return props.relatedSystems.map((s, i) => {
            return <Skeleton height={80} width={"100%"} key={i} />;
        });
    }

    return (
        <>
            <DotLegalStepHeader bottomMargin>{translateString("associatedAssetsHeader")}</DotLegalStepHeader>

            {isLoading ? getLoadingState() : getSelectedSystems()}

            {isAddingNewRelation && (
                <DotLegalCollapse
                    isExpanded
                    onExpandClick={() => {}}
                    onRemoveClick={() => setIsAddingNewRelation(false)}
                    removeItemText={translateString("removeAsset")}
                >
                    <SystemRelationSelector
                        disabled={props.readOnly}
                        isLoading={isLoading}
                        isDetailsLoading={isDetailsLoading}
                        selectableItems={getSelectableItems()}
                        onAssetChange={(id) => handleRelationChange(id)}
                    />
                </DotLegalCollapse>
            )}

            <Box sx={styles.addSystem}>
                <DotLegalButton
                    disabled={isAddingNewRelation || props.disableSystemRelations || props.readOnly}
                    buttonType="secondary"
                    onClick={() => {
                        setIsAddingNewRelation(true);
                        setExpandedId("");
                    }}
                >
                    {translateString("addAsset")}
                </DotLegalButton>
            </Box>

            {props.relatedSystems.length === 0 && !isAddingNewRelation && (
                <Box sx={styles.checkboxContainer}>
                    <DotLegalCheckbox
                        disabled={props.readOnly}
                        onChange={(checked) => props.onDisableSystemRelationsChange(checked)}
                        label={translateString("disableSystemAssociatedAssets")}
                        checked={props.disableSystemRelations}
                    />
                </Box>
            )}
        </>
    );
}

export default SystemRelationStep;
